<template>
  <o-modal
    ref="modal"
    :title="isNewRecord ? $t('service.add') : $t('service.edit')"
    :info="$t('components.reservation.actions.editService.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.done'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg dark:bg-gray-900"
    content-class="mx-auto max-w-lg h-full flex flex-col bg-white dark:bg-gray-900"
  >
    <div v-if="reservationService" class="text-left h-full flex flex-col">
      <div class="-mx-8 mb-4 flex-grow h-10 -mt-5 border-b">
        <o-scroll class="pb-4 pt-5 px-8 h-full">
          <div v-if="reservationService.service && reservationService.employee" class="w-full bg-gray-50 border rounded-xl shadow-sm p-3 mb-6 dark:bg-gray-700/20">
            <div class="w-full flex flex-wrap md:flex-nowrap space-y-3 md:space-y-0 space-x-3 items-center">
              <div class="block self-stretch relative pr-1">
                <div
                  class="w-1 rounded h-full"
                  :style="{
                    backgroundColor: colour
                  }"
                />
              </div>

              <div class="pt-1">
                <p class="text-sm text-gray-700 dark:text-gray-200">
                  {{ startLabel }} - {{ finishLabel }}
                </p>
                <p class="text-base font-semibold leading-tight md:leading-loose my-1 md:my-0">
                  {{ reservationService.service.title }}
                </p>
                <div class="text-sm text-gray-600 dark:text-gray-300 flex flex-wrap leading-none">
                  <span class="flex items-center mr-4 mb-1 leading-tight">
                    <o-avatar :items="[ reservationService.employee ]" class="hidden md:flex pr-2" />
                    <span>{{ reservationService.employee.displayName }}</span>
                  </span>
                  <div class="flex items-center">
                    <span class="flex items-center mr-4 mb-1 leading-tight">
                      <o-icon class="hidden md:block mr-2" icon="menuBookings" />
                      <span>{{ durationLabel }}</span>
                    </span>
                    <span class="flex items-center mr-4 mb-1 leading-tight">
                      <o-icon class="hidden md:block mr-2" icon="price" />
                      <span>{{ priceLabel }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="p-3 bg-gray-50 rounded-xl border dark:bg-gray-700/20">
            <o-form ref="form" class="h-full">
              <o-field-group :label="$t('general.service')" class="w-full mb-6">
                <service-select
                  v-model="reservationService.service"
                  :type="serviceType"
                  :add-on-mode="regularServiceAddOnModes"
                  class="w-full bg-white mb-6 dark:bg-gray-900"
                  @change="serviceChanged"
                />
              </o-field-group>

              <o-field-group :label="$t('general.employee')" class="w-full mb-6">
                <employee-select
                  v-model="reservationService.employee"
                  class="w-full bg-white mb-6 dark:bg-gray-900"
                  :options="employeeOptions"
                  :disabled="!hasService"
                  :preselect="true"
                  :placeholder="$t('employee.select')"
                  :availability-options="{
                    service: reservationService,
                    date: reservation.date.value,
                    reservations: [reservation],
                    startTime: reservationService.startTime
                  }"
                  @change="employeeChanged"
                />
              </o-field-group>

              <div class="flex items-center space-x-4 mb-6">
                <o-field-group
                  :label="$t('general.startTime')"
                  :name="$t('general.startTime')"
                  rules="required"
                  :validation-value="reservationService.startTime"
                  class="w-full md:w-1/2"
                >
                  <template #default="{ status }">
                    <time-field v-model="reservationService.startTime" :disabled="!hasService" :status="status" :augmenter="Augmenters.minutes" />
                  </template>
                </o-field-group>

                <o-field-group
                  :label="$t('general.price')"
                  :name="$t('general.price')"
                  rules="required"
                  :validation-value="reservationService.price"
                  :modified="isPriceModified"
                  :modified-message="$t('service.priceModified')"
                  class="w-full md:w-1/2"
                >
                  <template #default="{ status }">
                    <currency-field v-model="reservationService.price" :disabled="!hasService" :status="status" />
                  </template>
                </o-field-group>
              </div>

              <div class="flex items-center space-x-4 mb-6">
                <o-field-group :label="$t('general.durationMinutes')" :modified="isDurationModified" :modified-message="$t('service.durationModified')" class="w-1/3 px-1 py-1">
                  <number-field v-model="reservationService.duration" slim :min="0" />
                </o-field-group>

                <o-field-group :label="$t('components.reservation.actions.editService.processing')" :modified="isProcessingModified" :modified-message="$t('service.processingModified')" class="w-1/3 px-1 py-1">
                  <number-field v-model="reservationService.processing" slim />
                </o-field-group>

                <o-field-group :label="$t('components.reservation.actions.editService.finishing')" :modified="isFinishingModified" :modified-message="$t('service.finishingModified')" class="w-1/3 px-1 py-1">
                  <number-field v-model="reservationService.finishing" slim />
                </o-field-group>
              </div>

              <div class="flex items-center space-x-4 mb-6">
                <o-field-group :modified="isPreBufferModified" :modified-message="$t('service.preBufferModified')" :label="$t('service.preBuffer')" class="w-1/5 px-1 py-1">
                  <number-field v-model="reservationService.bufferTimePre" slim />
                </o-field-group>

                <o-field-group :modified="isPostBufferModified" :modified-message="$t('service.postBufferModified')" :label="$t('service.postBuffer')" class="w-1/5 px-1 py-1">
                  <number-field v-model="reservationService.bufferTimePost" slim />
                </o-field-group>
              </div>

              <template v-if="reservationService.resources">
                <div class="w-full flex flex-wrap mt-4 -mx-2">
                  <template v-for="(reservationResource, reservationResourceIndex) in reservationService.resources" :key="reservationResourceIndex">
                    <o-field-group

                      :label="reservationResource.serviceResource.name"
                      class="w-full md:w-1/2 px-2 mb-3"
                    >
                      <resource-select
                        v-model="reservationResource.resource"
                        class="w-full bg-white shadow-sm rounded dark:bg-gray-900"
                        :key="reservationResourceIndex + '-' + reservationResource.serviceResource.idServiceResource"
                        :disabled="!hasService"
                        :service="reservationService"
                        :resource="reservationResource"
                        :availability-options="{
                          service: reservationService,
                          date: reservation.date.value,
                          resource: reservationService.resources[reservationResourceIndex],
                          reservations: [reservation],
                          startTime: reservationService.startTime
                        }"
                        :preselect="isNewRecord || hasServiceChanged || hasEmployeeChanged"
                        allow-none
                        @change="handleResourceChange"
                      />
                    </o-field-group>
                  </template>
                </div>
              </template>
            </o-form>
          </div>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import EmployeeSelect from '@/components/employee/elements/Select.vue'
import ResourceSelect from '@/components/resource/elements/Select.vue'
import ServiceSelect from '@/components/service/elements/Select.vue'
import { OAvatar } from '@/components/elements'
import {
  CurrencyField,
  TimeField,
  NumberField,
} from '@/components/fields'

import { ServiceResourceAssignment } from '@/_api/models/serviceResource'
import { ServiceAddOnMode } from '@/_api/models/service'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  loading?: boolean
  serviceType?: string | null
  reservation: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  serviceType: null,
})

const instance = getCurrentInstance()!
const { $auth } = useNuxtApp() as any

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

const modal = ref<any>()
const form = ref<any>()

const reservationService = ref<any>(null)
const resources = ref<any>(null)
const callback = ref<Function | null>(null)
const isNewRecord = ref(false)
const hasServiceChanged = ref(false)
const hasEmployeeChanged = ref(false)

const regularServiceAddOnModes = [
  ServiceAddOnMode.notAllowed,
  ServiceAddOnMode.allowed,
]

const services = computed(() => serviceStore.all)
const employees = computed(() => employeeStore.all)

const serviceDefaults = computed(() => {
  const service = reservationService.value?.service

  if (!service) {
    return null
  }

  const found = services.value
    .find(({ idService }: any) => idService === service.idService)

  return found
})

const colour = computed(() => {
  const service = reservationService.value.service
  const c = service ? service.borderHexColour : 'bbbbbb'

  return '#' + c
})

const employeeOptions = computed(() => {
  const defaults = serviceDefaults.value
  const emps = employees.value

  if (!defaults) {
    return []
  }

  const employeeIDs = defaults.employeeIDs

  return emps
    .filter((employee: any) => employee.isEmployeeType)
    .filter(({ idEmployee }: any) => employeeIDs.includes(idEmployee))
})

const priceLabel = computed(() => {
  return $auth.info.location.currencySymbol + reservationService.value.price
})

const startLabel = computed(() => {
  return reservationService.value.startLabel
})

const finishLabel = computed(() => {
  return reservationService.value.finishLabel
})

const durationLabel = computed(() => {
  return reservationService.value.durationLabel
})

const hasService = computed(() => {
  return !!reservationService.value?.service
})

const hasEmployee = computed(() => {
  return !!reservationService.value?.employee
})

const hasResources = computed(() => {
  return !!reservationService.value?.resources
})

const isDisabled = computed(() => {
  return !hasService.value || !hasEmployee.value
})

const isPriceModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.price !== serviceDefaults.value.price
})

const isTimingModified = computed(() => {
  return hasService.value && serviceDefaults.value && (isDurationModified.value || isProcessingModified.value || isFinishingModified.value || isPreBufferModified.value || isPostBufferModified.value)
})

const isDurationModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.duration !== serviceDefaults.value.duration
})

const isProcessingModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.processing !== serviceDefaults.value.processing
})

const isFinishingModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.finishing !== serviceDefaults.value.finishing
})

const isPreBufferModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.bufferTimePre !== serviceDefaults.value.bufferTimePre
})

const isPostBufferModified = computed(() => {
  return hasService.value && serviceDefaults.value && reservationService.value.bufferTimePost !== serviceDefaults.value.bufferTimePost
})

const isServiceCustomerTypeSingle = computed(() => {
  return reservationService.value?.isServiceCustomerTypeSingle
})

const isReservationClass = computed(() => {
  return props.reservation.isGroup
})

/* watch: {
  'reservationService.service': {
    handler (service, oldService) {
      if ((oldService && service && service.idService === oldService.idService) || !reservationService.value.service) {
        return false
      }

      const assignment = isReservationClass.value || reservationService.value.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer
      reservationService.value.setValues({ startTime: reservationService.value.startTime })
      reservationService.value.setResources(assignment, reservationService.value.resources)
    },
    deep: true
  },
  'reservationService.employee': {
    handler (employee, oldEmployee) {
      if ((oldEmployee && employee && employee.idEmployee === oldEmployee.idEmployee) || !reservationService.value.service) {
        return false
      }

      const assignment = isReservationClass.value || reservationService.value.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer

      reservationService.value.setValues({ startTime: reservationService.value.startTime })
      reservationService.value.setResources(assignment, reservationService.value.resources)
    },
    deep: true
  }
} */

onMounted(() => {
  serviceStore.sync()
  employeeStore.sync()
})

function serviceChanged () {
  hasServiceChanged.value = true

  const assignment = isReservationClass.value || reservationService.value.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer

  reservationService.value.setValues({ startTime: reservationService.value.startTime })
  reservationService.value.setResources(assignment, reservationService.value.resources, false)
}

function employeeChanged () {
  hasEmployeeChanged.value = true

  const assignment = isReservationClass.value || reservationService.value.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer

  reservationService.value.setValues({ startTime: reservationService.value.startTime })
  reservationService.value.setResources(assignment, reservationService.value.resources, false)
}

function open (data: any, cb: Function) {
  const rs = data.copy()

  if (!data.service) {
    isNewRecord.value = true
  } else {
    isNewRecord.value = false
  }

  if (!rs.employee) {
    rs.employee = null
  }

  if (rs.service) {
    const defaults = !rs.service
      ? null
      : services.value
        .find(({ idService }: any) => idService === rs.service.idService)

    if (defaults) {
      rs.service = defaults
    }

    const assignment = isReservationClass.value || rs.service.isServiceCustomerTypeSingle ? ServiceResourceAssignment.reservation : ServiceResourceAssignment.customer

    rs.setResources(assignment, rs.resources, false)
  }

  reservationService.value = rs
  callback.value = cb

  modal.value.open()
}

function validate () {
  return form.value.validate()
}

function handleConfirm () {
  const rs = reservationService.value.copy()

  const filteredResources: any[] = []

  for (const index in rs.resources) {
    const resource = rs.resources[index]
    if (resource.resource !== -1) {
      filteredResources.push(resource)
    }
  }

  rs.resources = filteredResources

  callback.value!(rs)
  return true
}

function handleResourceChange () {
  instance.proxy!.$forceUpdate()
}

defineExpose({ open })
</script>
